import { FluidModalAction, FluidSize, FluidTheme } from '@lmig/fluid-core';
import { FluidModalTranslationOptions } from '@lmig/fluid-core/lib/i18n/types/fluid-element-translations.type';
import { FunctionalComponent, h, VNode } from '@stencil/core';

import { CloseButton } from '../../../common/fluid-common-button.components';
import { FluidFunctionalComponentProps } from '../../../model/functional/fluid-functional-component-props.interface';
import { FluidI18nStore } from '../../../providers/fluid-i18n-provider/store/fluid-i18n.store';

// ================================================================ //
// -- Public Functional Components

/**
 * ModalContainer
 */
interface ModalProps extends FluidFunctionalComponentProps {
  modalTitle: string;
  limitOverflow: boolean;
  size: FluidSize;
  setContainerRef?: (el: HTMLDivElement) => void;
}
export const ModalContainer: FunctionalComponent<ModalProps> = (
  props: ModalProps,
  children: VNode[]
) => {
  const { modalTitle, size, setContainerRef, limitOverflow, identifier } =
    props;

  return (
    <div
      role='dialog'
      aria-labelledby={modalTitle ? 'modal-title' : null}
      ref={setContainerRef}
      class={{
        [`modal-container modal-${size}`]: true,
        'limit-overflow': limitOverflow,
      }}
      data-test-id={`${identifier}-modal-container`}>
      {children}
    </div>
  );
};

/**
 * ModalOverlay
 */
interface ModalOverlayProps {
  handleOutsideClick: () => void;
  transparentOverlay: boolean;
  identifier?: string;
}
export const ModalOverlay: FunctionalComponent<ModalOverlayProps> = (
  props: ModalOverlayProps
) => {
  const { handleOutsideClick, transparentOverlay, identifier } = props;
  return (
    <div
      role='presentation'
      onClick={handleOutsideClick}
      class={{
        'page-overlay': true,
        'overlay-transparent': transparentOverlay,
      }}
      data-test-id={`${identifier}-modal-page-overlay`}
    />
  );
};

// ================================================================ //
// -- Private Functional Components

/**
 * ModalHeader
 */
interface ModalHeaderProps {
  modalTitle: string;
  i18nStore?: FluidI18nStore;
  fluidTranslate?: FluidModalTranslationOptions;
  actionsDisabled: boolean;
  shouldDisableClose: boolean;
  autoFocus: boolean;
  hideCloseIcon: boolean;
  theme: FluidTheme;
  handleDismissButtonClick: () => void;
  setDismissBtnRef: (el: HTMLFluidButtonElement) => void;
  identifier?: string;
}
export const ModalHeader: FunctionalComponent<ModalHeaderProps> = (
  props: ModalHeaderProps
) => {
  const { modalTitle, i18nStore, fluidTranslate, identifier } = props;
  return (
    <div
      class={{
        'fluid-modal-header': true,
        'header-empty': !modalTitle,
      }}
      data-test-id={`${identifier}-modal-header`}>
      {(modalTitle || fluidTranslate?.modalTitle) && (
        <h1
          id='modal-title'
          class='fluid-modal-title'
          data-test-id={`${identifier}-modal-title`}>
          {i18nStore?.translate(fluidTranslate?.modalTitle) || modalTitle}
        </h1>
      )}
      <ModalDismissButton
        title={
          i18nStore?.translate(fluidTranslate?.dismissButton?.title) ||
          'Close Modal'
        }
        {...props}
      />
    </div>
  );
};

/**
 * ModalDismissButton
 */
interface ModalDismissButtonProps extends ModalHeaderProps {
  title: string;
  identifier?: string;
}
const ModalDismissButton: FunctionalComponent<ModalDismissButtonProps> = (
  props: ModalDismissButtonProps
) => {
  const {
    title,
    actionsDisabled,
    shouldDisableClose,
    hideCloseIcon,
    theme,
    autoFocus,
    handleDismissButtonClick,
    setDismissBtnRef,
    identifier,
  } = props;
  const disabled = actionsDisabled && shouldDisableClose;

  return (
    <CloseButton
      identifier={`${identifier}-modal`}
      class='fluid-modal-dismiss'
      render={!hideCloseIcon}
      title={title}
      disabled={disabled}
      theme={theme}
      setRef={(el: HTMLFluidButtonElement) => autoFocus && setDismissBtnRef(el)}
      onClick={handleDismissButtonClick}
    />
  );
};

/**
 * ModalBody
 */
interface ModalBodyProps {
  limitOverflow: boolean;
  modalActions: FluidModalAction[];
  actionSetSlot?: HTMLSlotElement;
  actionsDisabled: boolean;
  theme: FluidTheme;
  handleActionClick: (action: FluidModalAction) => void;
  identifier?: string;
}
export const ModalBody: FunctionalComponent<ModalBodyProps> = (
  props: ModalBodyProps
) => {
  const {
    limitOverflow,
    modalActions,
    actionSetSlot,
    theme,
    actionsDisabled,
    handleActionClick,
    identifier,
  } = props;
  return [
    <div
      class={{
        'fluid-modal-content': true,
        'limit-overflow': limitOverflow,
      }}
      data-test-id={`${identifier}-modal-content`}>
      <slot data-test-id={`${identifier}-modal-content-slot`} />
    </div>,
    <div
      class={{
        'fluid-modal-footer': true,
        'footer-empty': !modalActions && !actionSetSlot,
      }}
      data-test-id={`${identifier}-modal-footer`}>
      <ModalActions
        identifier={identifier}
        modalActions={modalActions}
        theme={theme}
        actionsDisabled={actionsDisabled}
        handleActionClick={handleActionClick}
      />
    </div>,
  ];
};

/**
 * ModalActions
 */
interface ModalActionsProps {
  modalActions: FluidModalAction[];
  theme: FluidTheme;
  actionsDisabled: boolean;
  handleActionClick: (action: FluidModalAction) => void;
  identifier?: string;
}
const ModalActions: FunctionalComponent<ModalActionsProps> = (
  props: ModalActionsProps
) => {
  const {
    modalActions,
    theme,
    actionsDisabled,
    handleActionClick,
    identifier,
  } = props;

  if (!modalActions) {
    return (
      <slot name='action-set' data-test-id={`${identifier}-modal-action-set`} />
    );
  }

  return [
    ...modalActions.map((action: FluidModalAction, idx: number) => {
      return (
        <fluid-button
          data-test-id={`${identifier}-modal-button-${action.label}`}
          theme={theme}
          label={action.label}
          type={action.type}
          fluidTranslate={action.fluidTranslate}
          iconBefore={action.iconBefore}
          iconAfter={action.iconAfter}
          disabled={
            (actionsDisabled && !action.enableOnModalDisabled) ||
            action.disabled
          }
          onClick={() => handleActionClick(action)}
          removeHorizontalMargin={idx + 1 === modalActions.length}
          removeVerticalMargin={true}
          overrideGlobalTheme={true}
        />
      );
    }),
  ];
};
